import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox, FileDown, CheckCircle2, MessageSquare, XCircle, Star } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, apiError, downloadFile, formatEuro, formatPickup } from "@/lib/api";
import { WhatsAppButton } from "@/components/fleet/WhatsAppButton";
import { ContactInfo } from "@/components/fleet/RideCard";
import { ChatDialog } from "@/components/fleet/ChatDialog";
import { CancelDialog, ReviewDialog } from "@/components/fleet/RideDialogs";
import { ReportPanel } from "@/components/fleet/ReportPanel";
import { t } from "@/lib/i18n";

const ESCROW_LABEL = { released: "Escrow sbloccato", awaiting_payee: "90% in attesa email PayPal", refunded: "Rimborsata" };

const Status = ({ ride }) => {
  const cr = ride.cancel_request;
  if (ride.status === "cancelled") return <span className="ff-badge ff-badge-muted text-[11px]">Annullata{ride.escrow_status === "refunded" ? " • Rimborsata" : ""}</span>;
  if (cr?.status === "disputed") return <span className="ff-badge bg-orange-500/20 border-orange-500 text-orange-300 text-[11px]">Contenzioso aperto</span>;
  if (cr?.status === "pending") return <span className="ff-badge bg-amber-400/20 border-amber-400 text-amber-300 text-[11px]">Annullamento richiesto</span>;
  if (ride.status === "completed") return <span className="ff-badge ff-badge-green text-[11px]">Completata • {ESCROW_LABEL[ride.escrow_status] || "Escrow"}</span>;
  if (ride.status === "booked") return <span className="ff-badge ff-badge-cobalt text-[11px]">Bloccata • {ride.escrow_ref}</span>;
  return <span className="ff-badge ff-badge-green text-[11px]">{ride.invite_enabled ? "Privata • in attesa" : "Live • in attesa"}</span>;
};

const Action = ({ testid, onClick, busy, children, className = "" }) => (
  <button type="button" data-testid={testid} disabled={busy} onClick={onClick} className={`ff-btn-secondary w-full text-sm uppercase ${className}`}>{children}</button>
);

const useCall = (fn, ok, onChanged) => {
  const [busy, setBusy] = useState(false);
  return [busy, async () => {
    setBusy(true);
    try {
      const { data } = await fn();
      toast.success(typeof ok === "function" ? ok(data) : ok);
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }];
};

const CompleteButton = ({ ride, onChanged }) => {
  const [busy, run] = useCall(() => api.post(`/rides/${ride.id}/complete`),
    (d) => d.escrow_status === "released" ? "Corsa completata • 90% inviato su PayPal al cedente" : "Corsa completata • il 90% partirà appena il cedente inserisce l'email PayPal", onChanged);
  return (
    <button type="button" data-testid={`my-ride-complete-${ride.id}`} disabled={busy} onClick={run} className="ff-btn-primary w-full text-base uppercase">
      <CheckCircle2 size={20} /> {busy ? "…" : t("CORSA COMPLETATA")}
    </button>
  );
};

const CancelResponse = ({ ride, onChanged }) => {
  const [busyA, accept] = useCall(() => api.post(`/rides/${ride.id}/cancel/respond`, { accept: true }), "Annullamento accettato • rimborso avviato", onChanged);
  const [busyR, reject] = useCall(() => api.post(`/rides/${ride.id}/cancel/respond`, { accept: false }), "Annullamento rifiutato • contenzioso aperto", onChanged);
  return (
    <div data-testid={`my-ride-cancel-response-${ride.id}`} className="rounded-xl border border-amber-400/60 bg-amber-400/10 p-3 flex flex-col gap-2">
      <span className="text-sm text-white font-semibold">Il collega chiede l'annullamento: {ride.cancel_request.reason}{ride.cancel_request.note ? ` — ${ride.cancel_request.note}` : ""}</span>
      <div className="grid grid-cols-2 gap-2">
        <Action testid={`my-ride-cancel-accept-${ride.id}`} busy={busyA} onClick={accept} className="border-[#10B981]">{t("Accetta")}</Action>
        <Action testid={`my-ride-cancel-reject-${ride.id}`} busy={busyR} onClick={reject} className="border-red-500 text-red-300">{t("Rifiuta")}</Action>
      </div>
    </div>
  );
};

const MiniRide = ({ ride, onChanged, onDialog }) => {
  const { date, time } = formatPickup(ride.pickup_at);
  const cr = ride.cancel_request;
  const confirmed = ["booked", "completed"].includes(ride.status);
  const canCancel = (ride.status === "booked" && !["pending", "disputed"].includes(cr?.status) && new Date(ride.pickup_at) > new Date()) || (ride.status === "available" && ride.is_mine);
  return (
    <div data-testid={`my-ride-${ride.id}`} className="ff-card p-4 flex flex-col gap-2.5">
      <div className="flex items-center justify-between gap-2"><span className="text-sm font-semibold text-white capitalize">{date} • {time}</span><Status ride={ride} /></div>
      <div className="text-lg font-extrabold text-white">{ride.origin} <span className="text-[#10B981]">➔</span> {ride.destination}</div>
      <div className="flex items-center justify-between text-sm text-[#94A3B8]">
        <span>{ride.vehicle_category} • {ride.passengers} pax • {ride.luggage} valigie{ride.invite_pin ? ` • PIN ${ride.invite_pin}` : ""}</span>
        <span className="ff-display text-xl font-extrabold text-white">{formatEuro(ride.fare)}</span>
      </div>
      {ride.contact && <ContactInfo contact={ride.contact} />}
      {cr?.status === "pending" && !cr.by_me && ride.status === "booked" && <CancelResponse ride={ride} onChanged={onChanged} />}
      {ride.status === "booked" && ride.booked_by_me && cr?.status !== "disputed" && <CompleteButton ride={ride} onChanged={onChanged} />}
      {confirmed && <Action testid={`my-ride-chat-${ride.id}`} onClick={() => onDialog("chat", ride)} className="border-[#10B981]"><MessageSquare size={18} /> {t("Contatti & Chat")}</Action>}
      {ride.status === "completed" && !ride.reviewed_by_me && <Action testid={`my-ride-review-${ride.id}`} onClick={() => onDialog("review", ride)} className="border-amber-400 text-amber-300"><Star size={18} /> {t("Lascia una recensione")}</Action>}
      {confirmed && <Action testid={`my-ride-pdf-${ride.id}`} onClick={() => downloadFile(`/rides/${ride.id}/document`, `foglio-di-via-${ride.escrow_ref || ride.id}.pdf`).catch(() => toast.error("Documento non disponibile"))} className="border-[#3B82F6]"><FileDown size={18} /> {t("Scarica Foglio di Via B2B (PDF)")}</Action>}
      {ride.is_mine && ride.status === "available" && <WhatsAppButton ride={ride} testid={`my-ride-whatsapp-${ride.id}`} />}
      {canCancel && <button type="button" data-testid={`my-ride-cancel-${ride.id}`} onClick={() => onDialog("cancel", ride)} className="flex items-center justify-center gap-2 text-sm text-red-300 hover:text-red-200 py-1"><XCircle size={16} /> {ride.status === "available" ? t("Ritira corsa") : t("Richiedi annullamento")}</button>}
    </div>
  );
};

const List = ({ rides, empty, onChanged, onDialog }) =>
  rides.length ? (
    <div className="flex flex-col gap-3">{rides.map((r) => <MiniRide key={r.id} ride={r} onChanged={onChanged} onDialog={onDialog} />)}</div>
  ) : (
    <div className="flex flex-col items-center gap-2 py-12 text-[#94A3B8] text-sm"><Inbox size={24} />{empty}</div>
  );

export const MyRidesSheet = ({ open, onClose, refreshKey }) => {
  const [data, setData] = useState({ published: [], booked: [] });
  const [tick, setTick] = useState(0);
  const [dialog, setDialog] = useState(null);
  const onChanged = () => setTick((t) => t + 1);
  const onDialog = (kind, ride) => setDialog({ kind, ride });
  const closeDialog = () => setDialog(null);
  const dialogDone = () => { closeDialog(); onChanged(); };

  useEffect(() => {
    if (open) {
      api.get("/my-rides").then((r) => setData(r.data)).catch(() => toast.error("Impossibile caricare le corse"));
    }
  }, [open, refreshKey, tick]);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="my-rides-sheet" className="ff-glass-panel w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle className="ff-display text-2xl font-extrabold text-white">My Rides</SheetTitle>
          <SheetDescription className="text-[#94A3B8]">{t("Corse cedute, corse bloccate e Storico & Report.")}</SheetDescription>
        </SheetHeader>
        <Tabs defaultValue="booked" className="mt-6" data-testid="my-rides-tab-group">
          <TabsList className="grid grid-cols-3 bg-[#18181B] w-full h-12">
            <TabsTrigger value="booked" className="h-10 text-xs sm:text-sm" data-testid="my-rides-tab-booked">{t("Prenotate")} ({data.booked.length})</TabsTrigger>
            <TabsTrigger value="published" className="h-10 text-xs sm:text-sm" data-testid="my-rides-tab-published">{t("Cedute")} ({data.published.length})</TabsTrigger>
            <TabsTrigger value="report" className="h-10 text-xs sm:text-sm" data-testid="my-rides-tab-report">{t("Storico & Report")}</TabsTrigger>
          </TabsList>
          <TabsContent value="booked" className="mt-4"><List rides={data.booked} empty={t("Nessuna corsa bloccata.")} onChanged={onChanged} onDialog={onDialog} /></TabsContent>
          <TabsContent value="published" className="mt-4"><List rides={data.published} empty={t("Nessuna corsa ceduta.")} onChanged={onChanged} onDialog={onDialog} /></TabsContent>
          <TabsContent value="report" className="mt-4"><ReportPanel open={open} key={tick} /></TabsContent>
        </Tabs>
        {dialog?.kind === "chat" && <ChatDialog ride={dialog.ride} onClose={closeDialog} />}
        {dialog?.kind === "cancel" && <CancelDialog ride={dialog.ride} onClose={closeDialog} onDone={dialogDone} />}
        {dialog?.kind === "review" && <ReviewDialog ride={dialog.ride} onClose={closeDialog} onDone={dialogDone} />}
      </SheetContent>
    </Sheet>
  );
};
